<template>
  <ion-page>
    <div class="results-container">
      <!-- Clean Header -->
      <div class="page-header">
        <h1 class="page-title">Results</h1>
        <span class="results-count">{{ filteredGroups.length }} events</span>
      </div>

      <!-- Enhanced Search and Filters -->
      <div class="controls-section">
        <!-- Search and Filter Button Row -->
        <div class="search-filter-row">
          <div class="search-container">
            <ion-searchbar
              v-model="searchText"
              mode="ios"
              class="clean-search"
              placeholder="Search by event name, sport, or venue..."
              @ionInput="handleSearch"
              :debounce="300"
            />
          </div>

          <button class="filter-button" @click="openFilterPopover">
            <ion-icon :icon="funnelOutline" class="filter-icon"></ion-icon>
            <span class="filter-text">Filters</span>
            <span class="filter-count" v-if="activeFilterCount > 0">
              ({{ activeFilterCount }})
            </span>
          </button>
        </div>

        <!-- Active Filters Display -->
        <div class="active-filters" v-if="hasActiveFilters">
          <div class="filter-tags">
            <span class="filter-tag" v-if="selectedSportFilter !== 'all'">
              Sport: {{ formatSportName(selectedSportFilter) }}
              <button @click="setSportFilter('all')" class="remove-filter">×</button>
            </span>
            <span class="filter-tag" v-if="selectedSeasonFilter !== 'all'">
              Season: {{ selectedSeasonFilter }}
              <button @click="setSeasonFilter('all')" class="remove-filter">×</button>
            </span>
            <span class="filter-tag" v-if="selectedTournamentFilter !== 'all'">
              Tournament: {{ getTournamentName(selectedTournamentFilter) }}
              <button @click="setTournamentFilter('all')" class="remove-filter">×</button>
            </span>
            <span class="filter-tag" v-if="selectedGroupNameFilter !== 'all'">
              Group: {{ selectedGroupNameFilter }}
              <button @click="setGroupNameFilter('all')" class="remove-filter">×</button>
            </span>
            <span class="filter-tag" v-if="selectedDateFilter !== 'all' || customDate">
              Date: {{ getActiveDateFilterText() }}
              <button @click="clearDateFilter" class="remove-filter">×</button>
            </span>
            <span class="filter-tag" v-if="searchText.trim()">
              Search: "{{ searchText }}"
              <button @click="searchText = ''" class="remove-filter">×</button>
            </span>
          </div>
        </div>

        <!-- Filter Popover Modal -->
        <div class="filter-overlay" v-if="showFilterPopover" @click="closeFilterPopover">
          <div class="filter-modal" @click.stop>
            <div class="filter-header">
              <h3>Filters</h3>
              <button @click="closeFilterPopover" class="close-btn">×</button>
            </div>

            <div class="filter-content">
              <!-- Sport Filter Section -->
              <div class="filter-section">
                <label class="filter-section-label">Sport</label>
                <select v-model="selectedSportFilter" class="filter-select">
                  <option value="all">All Sports</option>
                  <option v-for="sport in availableSports" :key="sport" :value="sport">
                    {{ formatSportName(sport) }}
                  </option>
                </select>
              </div>

              <!-- Season Filter Section -->
              <div class="filter-section">
                <label class="filter-section-label">Season</label>
                <select v-model="selectedSeasonFilter" class="filter-select">
                  <option value="all">All Seasons</option>
                  <option v-for="season in availableSeasons" :key="season._id" :value="season._id">
                    {{ season._id }}
                  </option>
                </select>
              </div>

              <!-- Tournament Filter Section -->
              <div class="filter-section">
                <label class="filter-section-label">Tournament</label>
                <select v-model="selectedTournamentFilter" class="filter-select">
                  <option value="all">All Tournaments</option>
                  <option v-for="tournament in tournaments" :key="tournament._id" :value="tournament._id">
                    {{ tournament.name }}
                  </option>
                </select>
              </div>

              <!-- Result Group Name Filter Section -->
              <div class="filter-section">
                <label class="filter-section-label">Result Group</label>
                <select v-model="selectedGroupNameFilter" class="filter-select">
                  <option value="all">All Result Groups</option>
                  <option v-for="name in availableGroupNames" :key="name" :value="name">
                    {{ name }}
                  </option>
                </select>
              </div>

              <!-- Date Filter Section -->
              <div class="filter-section">
                <label class="filter-section-label">Date Range</label>

                <!-- Quick Date Options -->
                <div class="quick-dates-grid">
                  <button @click="setQuickDate('today')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'today' }">Today</button>
                  <button @click="setQuickDate('week')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'week' }">This Week</button>
                  <button @click="setQuickDate('month')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'month' }">This Month</button>
                  <button @click="setQuickDate('past')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'past' }">Past Events</button>
                  <button @click="setQuickDate('upcoming')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'upcoming' }">Upcoming</button>
                  <button @click="setQuickDate('all')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'all' }">All Time</button>
                </div>

                <!-- Custom Date Input -->
                <div class="custom-date-section">
                  <label class="date-label">Or select a specific date:</label>
                  <input
                    type="date"
                    v-model="tempCustomDate"
                    class="date-input"
                    @change="selectCustomDate"
                  />
                </div>
              </div>
            </div>

            <div class="filter-actions">
              <button @click="clearAllFilters" class="clear-btn">Clear All</button>
              <button @click="closeFilterPopover" class="cancel-btn">Cancel</button>
              <button @click="applyFilters" class="apply-btn">Apply</button>
            </div>
          </div>
        </div>

        <!-- Date Picker Modal (kept for backward compatibility) -->
        <div class="date-picker-overlay" v-if="showDatePicker" @click="closeDatePicker">
          <div class="date-picker-modal" @click.stop>
            <div class="date-picker-header">
              <h3>Select Date</h3>
              <button @click="closeDatePicker" class="close-btn">×</button>
            </div>
                        <div class="date-picker-content">
              <!-- Quick Date Options -->
              <div class="quick-dates">
                <button @click="setQuickDate('today')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'today' }">Today</button>
                <button @click="setQuickDate('week')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'week' }">This Week</button>
                <button @click="setQuickDate('month')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'month' }">This Month</button>
                <button @click="setQuickDate('past')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'past' }">Past Events</button>
                <button @click="setQuickDate('upcoming')" class="quick-date-btn" :class="{ active: selectedDateFilter === 'upcoming' }">Upcoming</button>
              </div>

              <!-- Custom Date Input -->
              <div class="custom-date-section">
                <label class="date-label">Or select a specific date:</label>
                <input
                  type="date"
                  v-model="tempCustomDate"
                  class="date-input"
                  @change="selectCustomDate"
                />
              </div>

              <div class="date-picker-actions">
                <button @click="clearCustomDate" class="secondary-btn">Clear All</button>
                <button @click="applyCustomDate" class="primary-btn">Apply</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Clean Results List -->
      <ion-content class="results-content" :scroll-events="true">
        <!-- Loading State -->
        <div class="loading-state" v-if="isInitialLoading">
          <div class="loading-content">
            <ion-spinner name="crescent" class="loading-spinner"></ion-spinner>
            <h3>Loading groups...</h3>
            <p>Please wait while we fetch the latest results</p>
          </div>
        </div>

        <!-- Results List -->
        <div class="results-list" v-else-if="filteredGroups.length > 0">
          <div
            class="event-card"
            v-for="group in filteredGroups"
            :key="group._id"
            @click="selectGroup(group)"
          >
            <div class="card-content">
              <div class="event-icon">
                <ion-icon :icon="getSportIcon(group.type)"></ion-icon>
              </div>

              <div class="event-info">
                <div class="event-header">
                  <h3 class="event-title">{{ group.name || 'Unnamed Event' }}</h3>
                  <div class="event-badges">
                    <span class="status-badge" :class="getStatusClass(group.status)">
                      {{ formatStatusName(group.status) }}
                    </span>
                    <span class="event-date" v-if="group.dateTime">{{ formatDate(group.dateTime) }}</span>
                  </div>
                </div>
                <div class="event-meta">
                  <span class="sport-type">{{ formatSportName(group.type) }}</span>
                  <span class="separator">•</span>
                  <span class="meta-info">{{ getSportInfo(group) }}</span>
                  <span class="separator" v-if="group.venue">•</span>
                  <span class="venue-info" v-if="group.venue">{{ group.venue.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Empty State -->
        <div class="empty-state" v-else>
          <ion-icon :icon="searchOutline" class="empty-icon"></ion-icon>
          <h3>No events found</h3>
          <p v-if="hasActiveFilters">Try adjusting your search or filters</p>
          <p v-else>No events are currently available</p>
          <button class="clear-filters-btn" @click="clearAllFilters" v-if="hasActiveFilters">
            Clear All Filters
          </button>
        </div>
      </ion-content>
    </div>
  </ion-page>
</template>

<script>
import { defineComponent } from 'vue';
import {
  IonContent,
  IonIcon,
  IonSearchbar,
  IonPage,
  IonSpinner
} from '@ionic/vue';
import {
  golfOutline,
  bicycleOutline,
  tennisballOutline,
  peopleOutline,
  ribbonOutline,
  locationOutline,
  searchOutline,
  fitnessOutline,
  chevronBackOutline,
  chevronForwardOutline,
  calendarOutline,
  funnelOutline
} from 'ionicons/icons';
import moment from 'moment';
import _ from 'lodash';

export default defineComponent({
  name: 'ResultsList',
  components: {
    IonContent,
    IonIcon,
    IonSearchbar,
    IonPage,
    IonSpinner
  },
  data() {
    return {
      groups: [],
      tournaments: [],
      searchText: '',
      selectedSportFilter: 'all',
      selectedSeasonFilter: 'all',
      selectedTournamentFilter: 'all',
      selectedGroupNameFilter: 'all',
      selectedDateFilter: 'all',
      isInitialLoading: true,
      golfOutline,
      bicycleOutline,
      tennisballOutline,
      peopleOutline,
      ribbonOutline,
      locationOutline,
      searchOutline,
      fitnessOutline,
      chevronBackOutline,
      chevronForwardOutline,
      calendarOutline,
      funnelOutline,
      showDatePicker: false,
      showFilterPopover: false,
      tempCustomDate: '',
      customDate: false
    };
  },
  mounted() {
    this.initializeSeasonFilter();
    this.fetchGroups();
    this.fetchTournaments();
  },
  watch: {
    selectedSportFilter() {
      // Reset group name filter if it's no longer available
      if (this.selectedGroupNameFilter !== 'all' &&
          !this.availableGroupNames.includes(this.selectedGroupNameFilter)) {
        this.selectedGroupNameFilter = 'all';
      }
    },
    selectedTournamentFilter() {
      // Reset group name filter if it's no longer available
      if (this.selectedGroupNameFilter !== 'all' &&
          !this.availableGroupNames.includes(this.selectedGroupNameFilter)) {
        this.selectedGroupNameFilter = 'all';
      }
    },
    selectedSeasonFilter() {
      this.fetchGroups();
    }
  },
  computed: {
    availableSeasons() {
      return this.$store.getters['national/seasons'] || [];
    },

    availableSports() {
      const sports = [...new Set(this.groups.map(group => group.type).filter(Boolean))];
      return sports.sort();
    },

    availableGroupNames() {
      // Start with all groups
      let filteredGroups = [...this.groups];

      // Apply sport filter if selected
      if (this.selectedSportFilter !== 'all') {
        filteredGroups = filteredGroups.filter(group => group.type === this.selectedSportFilter);
      }

      // Apply tournament filter if selected
      if (this.selectedTournamentFilter !== 'all') {
        filteredGroups = filteredGroups.filter(group => group.tournamentId === this.selectedTournamentFilter);
      }

      // Extract unique group names from filtered groups
      const groupNames = [...new Set(filteredGroups.map(group => group.name).filter(Boolean))];
      return groupNames.sort();
    },

    hasActiveFilters() {
      return this.selectedSportFilter !== 'all' ||
             this.selectedSeasonFilter !== 'all' ||
             this.selectedTournamentFilter !== 'all' ||
             this.selectedGroupNameFilter !== 'all' ||
             this.selectedDateFilter !== 'all' ||
             this.searchText.trim();
    },

    filteredGroups() {
      let filtered = [...this.groups];

      // Apply search filter
      if (this.searchText.trim()) {
        const search = this.searchText.toLowerCase();
        filtered = filtered.filter(group =>
          (group.name || '').toLowerCase().includes(search) ||
          (group.type || '').toLowerCase().includes(search) ||
          (group.venue?.name || '').toLowerCase().includes(search)
        );
      }

      // Apply sport filter
      if (this.selectedSportFilter !== 'all') {
        filtered = filtered.filter(group => group.type === this.selectedSportFilter);
      }

      // Apply season filter
      if (this.selectedSeasonFilter !== 'all') {
        filtered = filtered.filter(group => group.season === this.selectedSeasonFilter);
      }

      // Apply tournament filter
      if (this.selectedTournamentFilter !== 'all') {
        filtered = filtered.filter(group => group.tournamentId === this.selectedTournamentFilter);
      }

      // Apply group name filter
      if (this.selectedGroupNameFilter !== 'all') {
        filtered = filtered.filter(group => group.name === this.selectedGroupNameFilter);
      }

      // Apply date filter
      if (this.selectedDateFilter !== 'all') {
        const now = moment();
        filtered = filtered.filter(group => {
          if (!group.dateTime) return false;
          const eventDate = moment(parseInt(group.dateTime));

          switch (this.selectedDateFilter) {
          case 'today':
            return eventDate.isSame(now, 'day');
          case 'week':
            return eventDate.isSame(now, 'week');
          case 'month':
            return eventDate.isSame(now, 'month');
          case 'past':
            return eventDate.isBefore(now);
          case 'upcoming':
            return eventDate.isAfter(now);
          default:
            return true;
          }
        });
      }

      // Sort by order value
      return filtered.sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });
    },

    formatCurrentDateFilter() {
      const now = moment();
      switch (this.selectedDateFilter) {
      case 'today':
        return 'Today';
      case 'week':
        return `This Week (${now.format('MMM DD')} - ${now.endOf('week').format('MMM DD')})`;
      case 'month':
        return `This Month (${now.format('MMM DD')} - ${now.endOf('month').format('MMM DD')})`;
      case 'past':
        return 'Past Events';
      case 'upcoming':
        return 'Upcoming Events';
      case 'custom':
        return this.tempCustomDate ? moment(this.tempCustomDate).format('MMM DD, YYYY') : 'Custom Date';
      default:
        return 'All Time';
      }
    },

    canNavigateDate() {
      return this.selectedDateFilter === 'today' || this.selectedDateFilter === 'custom';
    },

    activeFilterCount() {
      let count = 0;
      if (this.selectedSportFilter !== 'all') count++;
      if (this.selectedSeasonFilter !== 'all') count++;
      if (this.selectedTournamentFilter !== 'all') count++;
      if (this.selectedGroupNameFilter !== 'all') count++;
      if (this.selectedDateFilter !== 'all') count++;
      if (this.searchText.trim()) count++;
      return count;
    }
  },
  methods: {
    initializeSeasonFilter() {
      const seasons = this.$store.getters['national/seasons'];
      const currentSeason = _.find(seasons, (season) => _.get(season, 'currentSeason', false) === true);
      if (currentSeason) {
        this.selectedSeasonFilter = currentSeason._id;
      }
    },

    handleSearch(event) {
      this.searchText = event.target.value;
    },

    setSportFilter(sport) {
      this.selectedSportFilter = sport;
    },

    setSeasonFilter(season) {
      this.selectedSeasonFilter = season;
    },

    setTournamentFilter(tournamentId) {
      this.selectedTournamentFilter = tournamentId;
    },

    setGroupNameFilter(groupName) {
      this.selectedGroupNameFilter = groupName;
    },

    getTournamentName(tournamentId) {
      const tournament = this.tournaments.find(t => t._id === tournamentId);
      return tournament ? tournament.name : 'Unknown Tournament';
    },

    clearAllFilters() {
      this.selectedSportFilter = 'all';
      this.selectedSeasonFilter = 'all';
      this.selectedTournamentFilter = 'all';
      this.selectedGroupNameFilter = 'all';
      this.selectedDateFilter = 'all';
      this.searchText = '';
      this.customDate = false;
      this.tempCustomDate = '';
    },

    openFilterPopover() {
      this.showFilterPopover = true;
    },

    closeFilterPopover() {
      this.showFilterPopover = false;
    },

    applyFilters() {
      this.closeFilterPopover();
    },

    formatSportName(sport) {
      if (!sport) return '';
      // Format sport names for better display
      const formatMap = {
        'Team H2H': 'Team Head-to-Head',
        'Athletics (Track)': 'Athletics',
        'Field Event': 'Field Events'
      };
      return formatMap[sport] || sport;
    },

    formatStatusName(status) {
      if (!status) return 'Unknown';
      // Capitalize and format status names
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    formatDate(timestamp) {
      return moment(parseInt(timestamp)).format('MMM DD, YYYY');
    },

    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('completed') || statusLower.includes('finished')) return 'completed';
      if (statusLower.includes('active') || statusLower.includes('progress') || statusLower.includes('live')) return 'active';
      if (statusLower.includes('scheduled') || statusLower.includes('upcoming')) return 'scheduled';
      if (statusLower.includes('cancelled') || statusLower.includes('suspended')) return 'cancelled';
      return 'default';
    },

    getSportIcon(type) {
      switch (type) {
      case 'Golf':
        return golfOutline;
      case 'Race':
      case 'Athletics':
      case 'Athletics (Track)':
        return bicycleOutline;
      case 'Tennis':
      case 'Badminton':
        return tennisballOutline;
      case 'Team H2H':
      case 'Soccer':
      case 'Basketball':
        return peopleOutline;
      case 'Field Event':
      case 'Gymnastics':
        return ribbonOutline;
      case 'Swimming':
        return fitnessOutline;
      default:
        return ribbonOutline;
      }
    },

    async fetchGroups() {
      try {
        const params = {};
        if (this.selectedSeasonFilter !== 'all') {
          params.season = this.selectedSeasonFilter;
        }
        const response = await this.$http.get('/assemble/api/v1/livescores/result-groups', { params });
        this.groups = response.data.data || [];
      } catch (error) {
        console.error('Error fetching groups:', error);
        this.groups = [];
      } finally {
        this.isInitialLoading = false;
      }
    },

    async fetchTournaments() {
      try {
        const response = await this.$http.get('/assemble/api/v1/livescores/tournaments');
        this.tournaments = response.data.data || [];
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        this.tournaments = [];
      }
    },

    getActiveDateFilterText() {
      switch (this.selectedDateFilter) {
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'past':
        return 'Past Events';
      case 'upcoming':
        return 'Upcoming Events';
      case 'custom':
        return this.tempCustomDate ? moment(this.tempCustomDate).format('MMM DD, YYYY') : 'Custom Date';
      default:
        return 'All Time';
      }
    },

    clearDateFilter() {
      this.selectedDateFilter = 'all';
      this.customDate = false;
    },

    openDatePicker() {
      this.showDatePicker = true;
    },

    closeDatePicker() {
      this.showDatePicker = false;
    },

    selectCustomDate() {
      this.customDate = true;
    },

    clearCustomDate() {
      this.tempCustomDate = '';
      this.customDate = false;
    },

    applyCustomDate() {
      this.selectedDateFilter = 'custom';
      this.customDate = true;
      this.closeDatePicker();
    },

    handleDateFilterChange() {
      if (this.selectedDateFilter === 'custom') {
        this.openDatePicker();
      }
    },

    previousDay() {
      if (this.selectedDateFilter === 'today') {
        // Navigate to previous day logic can be added here
        console.log('Navigate to previous day');
      }
    },

    nextDay() {
      if (this.selectedDateFilter === 'today') {
        // Navigate to next day logic can be added here
        console.log('Navigate to next day');
      }
    },

    setQuickDate(filterType) {
      this.selectedDateFilter = filterType;
      this.customDate = false;
      this.tempCustomDate = '';
      this.closeDatePicker();
    },

    getSportInfo(group) {
      switch (group.type) {
      case 'Golf':
        return `${group.rounds || 1} rounds`;
      case 'Race':
      case 'Athletics':
      case 'Field Event':
      case 'Gymnastics':
        return `${(group.participants || []).length} participants`;
      case 'Team H2H':
        return `${(group.matches || []).length} matches`;
      case 'Tennis':
      case 'Badminton':
        return `${group.singlesDoubles || 'Singles'}, ${group.numberOfSets || 3} sets`;
      default:
        return `${(group.participants || []).length} participants`;
      }
    },

    selectGroup(group) {
      this.$router.push(`/results/${group._id}`);
    }
  }
});
</script>

<style lang="scss" scoped>
.results-container {
  height: 100vh;
  background: #0f1419;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  padding: 2rem 2rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1e2328;

  .page-title {
    font-size: 2rem;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
  }

  .results-count {
    color: #9ca3af;
    font-size: 0.875rem;
    font-weight: 500;
  }
}

.controls-section {
  padding: 1.5rem 2rem;
  background: #0f1419;
  border-bottom: 1px solid #1e2328;

  .search-filter-row {
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1rem;
  }

  .search-container {
    flex: 1;
  }

  .filter-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #1a1f24;
    border: 1px solid #374151;
    border-radius: 8px;
    color: #e5e7eb;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    min-height: 44px;

    &:hover {
      border-color: #3b82f6;
      background: #1e293b;
    }

    .filter-icon {
      font-size: 1rem;
      color: #60a5fa;
    }

    .filter-count {
      color: #3b82f6;
      font-weight: 600;
    }
  }

  .clean-search {
    --background: #1a1f24;
    --color: #e5e7eb;
    --placeholder-color: #6b7280;
    --icon-color: #9ca3af;
    border-radius: 8px;
    margin-bottom: 1rem;
    border: 1px solid #374151;
  }

  .filters-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .filter-label {
    font-size: 1rem;
    font-weight: 500;
    color: #d1d5db;
    margin-bottom: 0.5rem;
  }

  .sport-dropdown {
    padding: 0.75rem 1rem;
    border: 1px solid #374151;
    border-radius: 8px;
    background: #1a1f24;
    color: #e5e7eb;
    font-size: 1rem;
    cursor: pointer;
    min-width: 200px;

    &:focus {
      outline: none;
      border-color: #3b82f6;
    }

    option {
      background: #1a1f24;
      color: #e5e7eb;
    }
  }

  .date-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .date-navigation {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .date-nav-btn {
    padding: 0.75rem;
    border: 1px solid #374151;
    background: #1a1f24;
    color: #d1d5db;
    border-radius: 8px;
    font-size: 1.125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: #3b82f6;
      color: #3b82f6;
      background: #1e293b;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .date-display {
    padding: 0.75rem 1rem;
    border: 1px solid #374151;
    border-radius: 8px;
    background: #1a1f24;
    color: #e5e7eb;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 44px;
    min-width: 200px;
    white-space: nowrap;

    &:hover {
      border-color: #3b82f6;
      background: #1e293b;
    }
  }

  .calendar-icon {
    font-size: 1.25rem;
    color: #60a5fa;
  }

  .date-filter {
    padding: 0.5rem 0.75rem;
    border: 1px solid #374151;
    border-radius: 6px;
    background: #1a1f24;
    color: #e5e7eb;
    font-size: 0.875rem;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #3b82f6;
    }

    option {
      background: #1a1f24;
      color: #e5e7eb;
    }
  }

  .filter-group:last-child {
    flex-grow: 1;
  }

  .active-filters {
    margin-top: 1rem;
    padding: 0.75rem;
    background: #1a1f24;
    border-radius: 6px;
    border: 1px solid #2d3748;

    .active-filters-label {
      font-size: 0.875rem;
      font-weight: 500;
      color: #d1d5db;
      margin-bottom: 0.5rem;
    }

    .filter-tags {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .filter-tag {
      padding: 0.25rem 0.5rem;
      background: #2d3748;
      border-radius: 6px;
      color: #d1d5db;
      font-size: 0.875rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.25rem;

      .remove-filter {
        padding: 0;
        margin-left: 0.25rem;
        background: none;
        border: none;
        color: #9ca3af;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
      }
    }
  }

  .clear-filters-btn {
    padding: 0.75rem 1.5rem;
    border: 1px solid #3b82f6;
    background: #1a1f24;
    color: #3b82f6;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 44px;

    &:hover {
      background: #3b82f6;
      color: white;
    }
  }
}

.results-content {
  --background: #0f1419;
  height: 100%;
  overflow-y: auto;
}

.results-list {
  padding: 1rem 1.5rem;
  min-height: 100%;
}

.event-card {
  background: #1a1f24;
  border: 1px solid #2d3748;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4a5568;
    background: #1e2328;
  }

  &:last-child {
    margin-bottom: 1rem;
  }
}

.card-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.event-icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  background: #2d3748;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  ion-icon {
    font-size: 1.125rem;
    color: #60a5fa;
  }
}

.event-info {
  flex: 1;
  min-width: 0;

  .event-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  .event-title {
    font-size: 1rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  .event-badges {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;

    &.completed {
      background: #065f46;
      color: #10b981;
    }

    &.active {
      background: #1e40af;
      color: #3b82f6;
    }

    &.scheduled {
      background: #7c2d12;
      color: #f97316;
    }

    &.cancelled {
      background: #7f1d1d;
      color: #ef4444;
    }

    &.default {
      background: #2d3748;
      color: #d1d5db;
    }
  }

  .event-date {
    font-size: 0.75rem;
    color: #9ca3af;
    font-weight: 500;
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  .event-meta {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: #d1d5db;
    line-height: 1.2;

    .separator {
      color: #6b7280;
    }

    .sport-type {
      color: #60a5fa;
      font-weight: 500;
    }

    .meta-info, .venue-info {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  min-height: 400px;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .loading-spinner {
    --color: #3b82f6;
    width: 2.5rem;
    height: 2.5rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #d1d5db;
    margin: 0;
  }

  p {
    color: #9ca3af;
    margin: 0;
    font-size: 0.875rem;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;

  .empty-icon {
    font-size: 3rem;
    color: #4b5563;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #d1d5db;
    margin: 0 0 0.5rem 0;
  }

  p {
    color: #9ca3af;
    margin: 0;
    font-size: 0.875rem;
  }
}

// Mobile Responsiveness
@media (max-width: 768px) {
  .page-header {
    padding: 1.5rem 1rem 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;

    .page-title {
      font-size: 1.75rem;
    }
  }

  .controls-section {
    padding: 1rem;

    .search-filter-row {
      flex-direction: column;
      gap: 0.75rem;
      align-items: stretch;
    }

    .filter-button {
      justify-content: center;
      padding: 1rem;
      font-size: 1rem;
      min-height: 48px;

      .filter-text {
        font-size: 1rem;
      }
    }

    .active-filters {
      .filter-tags {
        gap: 0.375rem;
      }
    }
  }

  .results-list {
    padding: 0.5rem 1rem;
  }

  .event-card {
    padding: 0.625rem;
    margin-bottom: 0.375rem;
  }

  .card-content {
    gap: 0.5rem;
  }

  .event-icon {
    width: 2rem;
    height: 2rem;

    ion-icon {
      font-size: 1rem;
    }
  }

  .event-info {
    .event-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.125rem;
      margin-bottom: 0.125rem;
    }

    .event-badges {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    .status-badge {
      font-size: 0.7rem;
      padding: 0.125rem 0.375rem;
    }

    .event-title {
      font-size: 0.9rem;
      white-space: normal;
      max-height: 2.4em;
      line-height: 1.2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      display: -webkit-box;
    }

    .event-date {
      font-size: 0.7rem;
      margin-left: 0;
    }

    .event-meta {
      font-size: 0.7rem;
      gap: 0.25rem;
    }
  }
}

@media (max-width: 480px) {
  .filter-btn {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
  }

  .results-list {
    padding: 0.25rem 0.5rem;
  }

  .event-card {
    padding: 0.5rem;
    margin-bottom: 0.25rem;
  }
}

// Filter Modal Styles
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-start;
  }
}

.filter-modal {
  background: #1a1f24;
  border-radius: 8px;
  border: 1px solid #374151;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: 100vh;
    height: 100dvh; // Use dynamic viewport height on mobile
    max-height: none;
    border-radius: 0;
    border: none;
  }
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #374151;

  h3 {
    margin: 0;
    color: #ffffff;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #ffffff;
    }
  }
}

.filter-content {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0; // Allow flex child to shrink

  @media (max-width: 768px) {
    padding: 1rem;
    padding-bottom: 0; // Remove bottom padding since actions are sticky
  }
}

.filter-section {
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
}

.filter-section-label {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #d1d5db;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
}

.filter-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #374151;
  border-radius: 8px;
  background: #0f1419;
  color: #e5e7eb;
  font-size: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: #0f1419;
    color: #e5e7eb;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1.125rem;
    min-height: 44px;
  }
}

.quick-dates-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.quick-date-btn {
  padding: 0.75rem 1rem;
  border: 1px solid #374151;
  border-radius: 6px;
  background: #1a1f24;
  color: #e5e7eb;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #3b82f6;
    background: #1e293b;
  }

  &.active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1rem;
    min-height: 44px;
  }
}

.custom-date-section {
  .date-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #d1d5db;
    margin-bottom: 0.5rem;
  }

  .date-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #374151;
    border-radius: 6px;
    background: #0f1419;
    color: #e5e7eb;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #3b82f6;
    }

    @media (max-width: 768px) {
      padding: 1rem;
      font-size: 1.125rem;
      min-height: 44px;
    }
  }
}

.filter-actions {
  display: flex;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #374151;
  flex-shrink: 0; // Prevent buttons from being hidden

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.5rem;
    position: sticky;
    bottom: 0;
    background: #1a1f24;
    border-top: 2px solid #374151;
  }

  .clear-btn, .cancel-btn, .apply-btn {
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    border: 1px solid;

    @media (max-width: 768px) {
      padding: 1rem;
      font-size: 1.125rem;
      min-height: 48px;
      flex: 1;
    }
  }

  .clear-btn {
    background: #374151;
    border-color: #374151;
    color: #d1d5db;

    &:hover {
      background: #4b5563;
      border-color: #4b5563;
    }

    @media (max-width: 768px) {
      flex: 0 0 auto;
      min-width: 80px;
    }
  }

  .cancel-btn {
    background: transparent;
    border-color: #6b7280;
    color: #d1d5db;

    &:hover {
      background: #374151;
      border-color: #9ca3af;
    }

    @media (max-width: 768px) {
      flex: 1;
    }
  }

  .apply-btn {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;

    &:hover {
      background: #2563eb;
      border-color: #2563eb;
    }

    @media (max-width: 768px) {
      flex: 1;
    }
  }
}

// Date Picker Styles
.date-picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.date-picker-modal {
  background: #1a1f24;
  border-radius: 8px;
  border: 1px solid #374151;
  min-width: 320px;
  max-width: 90vw;
}

.date-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #374151;

  h3 {
    margin: 0;
    color: #ffffff;
    font-size: 1.125rem;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #ffffff;
    }
  }
}

.date-picker-content {
  padding: 1.5rem;

  .quick-dates {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }

  .quick-date-btn {
    padding: 0.75rem 1rem;
    border: 1px solid #374151;
    border-radius: 6px;
    background: #1a1f24;
    color: #e5e7eb;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3b82f6;
      background: #1e293b;
    }

    &.active {
      background: #3b82f6;
      border-color: #3b82f6;
      color: white;
    }
  }

  .custom-date-section {
    margin-bottom: 1.5rem;
  }

  .date-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #d1d5db;
    margin-bottom: 0.5rem;
  }

  .date-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #374151;
    border-radius: 6px;
    background: #0f1419;
    color: #e5e7eb;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #3b82f6;
    }
  }

  .date-picker-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;

    .secondary-btn, .primary-btn {
      padding: 0.5rem 1rem;
      border-radius: 6px;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .secondary-btn {
      background: #374151;
      border: 1px solid #374151;
      color: #d1d5db;

      &:hover {
        background: #4b5563;
        border-color: #4b5563;
      }
    }

    .primary-btn {
      background: #3b82f6;
      border: 1px solid #3b82f6;
      color: white;

      &:hover {
        background: #2563eb;
        border-color: #2563eb;
      }
    }
  }
}
</style>